import { subscribeDesktopRealtime } from "./desktop-event-gateway";
import {
  ParsedAudioEvent,
  ParsedDesktopEvent,
  ParsedTranscriptEvent,
} from "./desktop-event-parser";
import { DesktopAudioAssembler } from "./desktop-audio-assembler";
import { DesktopTranscriptAssembler } from "./desktop-transcript-assembler";

import { electronModule } from "@/modules/electron";
import type { TimestampedAssemblyAITurnMessage } from "@/domains/dexie-db/audio-capture/types";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { Uuid } from "@/domains/global/identifiers";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";

export type DesktopCaptureCallbacks<TTranscript = TimestampedAssemblyAITurnMessage> = {
  onTranscript: (channel: 0 | 1, turn: TTranscript) => void;
  onPcm?: (samples: Int16Array) => void;
  onDebugEvent?: (data: Record<string, unknown>, kind: string) => void;
};

type DesktopCaptureServiceOptions<TTranscript> = {
  transcriptMapper?: (turn: TimestampedAssemblyAITurnMessage) => TTranscript;
};

export class DesktopCaptureService<TTranscript = TimestampedAssemblyAITurnMessage> {
  private windowId: string | null = null;
  private detectedMeetingWindowId: string | null = null;
  private uploadToken: string | null = null;
  private unsubscribe: (() => void) | null = null;
  private assembler = new DesktopAudioAssembler();
  private transcriptAssembler = new DesktopTranscriptAssembler();
  private stoppingPromise: Promise<void> | null = null;
  private activeWindowId: string | null = null;
  private preparedRecording: PreparedRecording;

  private callbacks: DesktopCaptureCallbacks<TTranscript>;
  private readonly mapTranscript: (turn: TimestampedAssemblyAITurnMessage) => TTranscript;

  constructor(
    preparedRecording: PreparedRecording,
    callbacks: DesktopCaptureCallbacks<TTranscript>,
    options: DesktopCaptureServiceOptions<TTranscript> = {}
  ) {
    this.preparedRecording = preparedRecording;
    this.callbacks = callbacks;
    this.mapTranscript =
      options.transcriptMapper ??
      ((turn: TimestampedAssemblyAITurnMessage) => turn as unknown as TTranscript);
  }

  isRecordingWindowId(windowId?: string): boolean {
    return this.detectedMeetingWindowId === windowId;
  }

  async prepare({
    associatedNoteId,
    associatedAudioRecordingId,
    detectedMeetingWindowId,
    abortSignal,
  }: {
    associatedAudioRecordingId: Uuid;
    associatedNoteId: Uuid | null;
    detectedMeetingWindowId?: string;
    customVocabulary?: string[];
    abortSignal?: AbortSignal;
  }): Promise<void> {
    this.transcriptAssembler.reset();
    const windowId = await this.preparedRecording.windowId;
    if (abortSignal?.aborted) return;
    logger.info({
      message: "[DesktopCaptureService] prepared",
      info: { windowId, associatedAudioRecordingId, associatedNoteId },
    });
    this.windowId = windowId;
    this.detectedMeetingWindowId = detectedMeetingWindowId ?? null;
    this.activeWindowId = windowId;

    const recallUploadCredentials = await this.preparedRecording.recallUploadCredentials;
    this.uploadToken = recallUploadCredentials.uploadToken;
    if (abortSignal?.aborted) return;

    this.unsubscribe = subscribeDesktopRealtime(event => this.onEvent(event));
  }

  async start(abortSignal?: AbortSignal): Promise<void> {
    if (abortSignal?.aborted) return;
    if (!this.windowId || !this.uploadToken) throw new Error("Desktop capture not prepared");
    this.callbacks.onDebugEvent?.(
      { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      "recording-starting"
    );
    await electronModule.recall.startRecording({
      windowId: this.windowId,
      uploadToken: this.uploadToken,
      prepared: this.preparedRecording.recallStarted,
    });
    if (abortSignal?.aborted) return;
    this.callbacks.onDebugEvent?.(
      { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      "recording-started"
    );
  }

  async pause(): Promise<void> {
    if (!this.windowId) return;
    await electronModule.recall.pause({ windowId: this.windowId });
    this.callbacks.onDebugEvent?.(
      { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      "recording-paused"
    );
  }

  async resume(): Promise<void> {
    if (!this.windowId) return;
    await electronModule.recall.resume({ windowId: this.windowId });
    this.callbacks.onDebugEvent?.(
      { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      "recording-resumed"
    );
  }

  async stop(): Promise<Blob> {
    await this.ensureSdkStopped();
    this.callbacks.onDebugEvent?.(
      { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      "recording-stopped"
    );
    const blob = await this.assembler.finalizeWav();
    this.cleanupAfterSession();
    return blob;
  }

  async cancel(): Promise<void> {
    await this.ensureSdkStopped();
    this.callbacks.onDebugEvent?.(
      { windowId: this.windowId, detectedMeetingWindowId: this.detectedMeetingWindowId },
      "recording-cancelled"
    );
    this.cleanupAfterSession();
  }

  dispose() {
    void this.cancel();
  }

  private async ensureSdkStopped(): Promise<void> {
    if (!this.windowId) {
      return;
    }
    if (this.stoppingPromise) {
      await this.stoppingPromise;
      return;
    }

    const windowId = this.windowId;
    this.stoppingPromise = (async () => {
      try {
        logger.info({
          message: "[DesktopCaptureService] stopping",
          info: { windowId },
        });
        await electronModule.recall.stop({ windowId });
      } catch (err) {
        logger.warn({
          message: "[DesktopCaptureService] stop failed",
          info: { error: objectModule.safeErrorAsJson(err as Error) },
        });
      } finally {
        if (this.windowId === windowId) {
          this.windowId = null;
          this.activeWindowId = null;
        }
        this.uploadToken = null;
        this.stoppingPromise = null;
      }
    })();
    await this.stoppingPromise;
  }

  private cleanupAfterSession() {
    try {
      this.unsubscribe?.();
    } catch (_) {
      /* empty */
    }
    this.unsubscribe = null;
    this.assembler.reset();
    this.transcriptAssembler.reset();
    this.windowId = null;
    this.uploadToken = null;
    this.activeWindowId = null;
  }

  flushPendingPcmBlob(): Blob | null {
    return this.assembler.flushPendingToPcmBlob();
  }

  getPendingPcmBlob(): Blob | null {
    return this.assembler.getPendingPcmBlob();
  }

  private onEvent(parsedEvent: ParsedDesktopEvent) {
    if (!parsedEvent) return;

    const event = parsedEvent as ParsedTranscriptEvent | ParsedAudioEvent;
    if (event.type === "audio_mixed_raw.data") {
      // Recall AI includes the originating windowId, but we currently allow only one
      // recording at a time. Capture any audio payload the SDK emits.
      const samples = this.assembler.appendBase64Pcm16(event.bufferBase64);
      this.callbacks.onPcm?.(samples);
      return;
    }
    const channel: 0 | 1 = event.speakerIsHost ? 0 : 1; // host → Me (0), participants → Other Speakers (1)
    const update = this.transcriptAssembler.ingest({ event });
    if (update) {
      const mappedTurn = this.mapTranscript(update.turn);
      this.callbacks.onTranscript(channel, mappedTurn);
    }
  }
}
